import { EthereumProvider } from '@walletconnect/ethereum-provider';
import {
  createPublicClient,
  createWalletClient,
  custom,
  defineChain,
  formatEther,
  getAddress,
  http,
  maxUint256,
  parseEther,
} from 'viem';
import { curveAbi, factoryAbi, feeEscrowAbi, tokenAbi, zeroAddress } from '../../../services/indexer/src/abi.mjs';

let config;
let provider;
let publicClient;
let walletClient;
let account;
const listeners = new Set();

async function api(path, options) {
  const response = await fetch(path, { ...options, headers: { 'content-type': 'application/json', ...(options?.headers ?? {}) } });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Request failed (${response.status})`);
  return body;
}

function chainFromConfig() {
  return defineChain({
    id: config.chainId,
    name: config.chainName,
    nativeCurrency: config.nativeCurrency,
    rpcUrls: { default: { http: [config.publicRpcUrl] } },
    blockExplorers: { default: { name: 'Blockscout', url: config.explorerUrl } },
  });
}

async function init() {
  config = await api('/api/config');
  publicClient = createPublicClient({ chain: chainFromConfig(), transport: http(config.publicRpcUrl) });
  return config;
}

function notify() {
  for (const listener of listeners) listener({ account, connected: Boolean(account) });
}

async function ensureChain(targetProvider) {
  const target = `0x${config.chainId.toString(16)}`;
  try {
    await targetProvider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: target }] });
  } catch (error) {
    if (error.code !== 4902 && !/unrecognized chain|not added/i.test(error.message ?? '')) throw error;
    await targetProvider.request({ method: 'wallet_addEthereumChain', params: [{
      chainId: target,
      chainName: config.chainName,
      nativeCurrency: config.nativeCurrency,
      rpcUrls: [config.publicRpcUrl],
      blockExplorerUrls: [config.explorerUrl],
    }] });
  }
}

async function connect(kind = 'browser') {
  if (!config) await init();
  if (kind === 'walletconnect') {
    if (!config.walletConnectProjectId) throw new Error('WalletConnect is not configured. Add NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID.');
    provider = await EthereumProvider.init({
      projectId: config.walletConnectProjectId,
      chains: [config.chainId],
      optionalChains: [config.chainId],
      showQrModal: true,
      metadata: { name: 'Oulta', description: 'Oulta launchpad', url: location.origin, icons: [] },
      rpcMap: { [config.chainId]: config.publicRpcUrl },
    });
    await provider.connect();
  } else {
    provider = window.ethereum;
    if (!provider?.request) throw new Error('No browser wallet was detected. Install an EIP-1193 wallet or use WalletConnect.');
    await ensureChain(provider);
  }
  const accounts = await provider.request({ method: 'eth_requestAccounts' });
  if (!accounts?.[0]) throw new Error('The wallet did not return an account');
  account = getAddress(accounts[0]);
  walletClient = createWalletClient({ account, chain: chainFromConfig(), transport: custom(provider) });
  provider.on?.('accountsChanged', values => {
    account = values?.[0] ? getAddress(values[0]) : null;
    if (account) walletClient = createWalletClient({ account, chain: chainFromConfig(), transport: custom(provider) });
    notify();
  });
  provider.on?.('chainChanged', chainId => {
    if (Number(chainId) !== config.chainId) notify();
  });
  provider.on?.('disconnect', () => { account = null; walletClient = null; notify(); });
  notify();
  return account;
}

async function disconnect() {
  await provider?.disconnect?.();
  account = null; walletClient = null; provider = null; notify();
}

function onWalletChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function requireWallet() {
  if (!walletClient || !account) throw new Error('Connect a wallet first');
  if (!config.chainVerified) throw new Error('Oulta contract configuration has not passed server verification');
}

function requireWrites(flag = 'launchWritesEnabled') {
  requireWallet();
  if (!config[flag]) throw new Error('This transaction type is disabled by the Oulta safety configuration');
}

async function wait(hash) {
  const receipt = await publicClient.waitForTransactionReceipt({ hash, confirmations: 1, timeout: 180_000 });
  if (receipt.status !== 'success') throw new Error('The transaction reverted');
  return receipt;
}

async function listTokens(params = '') {
  return (await api(`/api/tokens${params}`)).tokens;
}

async function tokenDetails(address) {
  const token = (await api(`/api/tokens/${getAddress(address)}`)).token;
  const [trades, candles] = await Promise.all([
    api(`/api/tokens/${token.address}/trades?limit=100`),
    api(`/api/tokens/${token.address}/candles?timeframe=1m`),
  ]);
  return { token, trades: trades.trades, candles: candles.candles };
}

async function walletProfile(address = account) {
  if (!address) throw new Error('Connect a wallet first');
  return api(`/api/wallet/${getAddress(address)}`);
}

async function signIn() {
  requireWallet();
  const challenge = await api('/api/auth/nonce', { method: 'POST', body: JSON.stringify({ address: account }) });
  const signature = await walletClient.signMessage({ account, message: challenge.message });
  await api('/api/auth/verify', {
    method: 'POST', body: JSON.stringify({ address: account, nonce: challenge.nonce, message: challenge.message, signature }),
  });
  return account;
}

async function uploadTokenImage(file) {
  if (!file) return '';
  if (!config.mediaUploadsEnabled) throw new Error('Token image storage is not configured');
  await signIn();
  const data = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
  return (await api('/api/media/token-image', {
    method: 'POST', body: JSON.stringify({ data, contentType: file.type }),
  })).url;
}

async function launchToken(form) {
  requireWrites('launchWritesEnabled');
  const pairToken = form.pairToken ? getAddress(form.pairToken) : zeroAddress;
  const quote = await api(`/api/launch/quote?pairToken=${pairToken}`);
  if (!quote.contractLaunchEnabled || !quote.launchConfig.enabled) throw new Error('New launches are disabled on the configured factory');
  const creatorTaxBps = Math.round(Number(form.creatorTaxPercent || 0) * 100);
  if (creatorTaxBps > Number(quote.maxCreatorTaxBps)) throw new Error('Creator tax exceeds the factory maximum');
  const logo = form.imageFile ? await uploadTokenImage(form.imageFile) : String(form.logo || '');
  const params = {
    name: String(form.name || '').trim(),
    symbol: String(form.symbol || '').trim().toUpperCase(),
    logo,
    description: String(form.description || '').trim(),
    socials: {
      twitter: String(form.twitter || '').trim(), telegram: String(form.telegram || '').trim(),
      discord: String(form.discord || '').trim(), website: String(form.website || '').trim(),
      farcaster: String(form.farcaster || '').trim(),
    },
    creatorFeeRecipient: form.creatorFeeRecipient ? getAddress(form.creatorFeeRecipient) : zeroAddress,
    creatorTaxBps,
    buybackEnabled: Boolean(form.buybackEnabled),
    expectedEconomics: quote.economics,
  };
  if (!params.name || !params.symbol) throw new Error('Token name and symbol are required');
  const { request } = await publicClient.simulateContract({
    account, address: config.factoryAddress, abi: factoryAbi, functionName: 'launchToken',
    args: [params, BigInt(config.launchConfigId), pairToken], value: BigInt(quote.launchFee),
  });
  return wait(await walletClient.writeContract(request));
}

function amountOut(amountIn, reserveIn, reserveOut) {
  if (amountIn <= 0n || reserveIn <= 0n || reserveOut <= 0n) throw new Error('Invalid trade amount or empty curve');
  return amountIn * reserveOut / (reserveIn + amountIn);
}

function withSlippage(value, slippageBps) {
  return value * BigInt(10_000 - slippageBps) / 10_000n;
}

async function buyToken({ curveAddress, amountEth, slippageBps = 100 }) {
  requireWrites('launchWritesEnabled');
  const curve = getAddress(curveAddress);
  const quoteIn = parseEther(String(amountEth));
  const [reserves, feeBps, taxBps, pairToken] = await Promise.all([
    publicClient.readContract({ address: curve, abi: curveAbi, functionName: 'getReserves' }),
    publicClient.readContract({ address: curve, abi: curveAbi, functionName: 'feeBps' }),
    publicClient.readContract({ address: curve, abi: curveAbi, functionName: 'creatorTaxBps' }),
    publicClient.readContract({ address: curve, abi: curveAbi, functionName: 'pairToken' }),
  ]);
  if (pairToken !== zeroAddress) throw new Error('ERC-20 quote trades require explicit allowance support and are not enabled in this UI');
  const net = quoteIn * (10_000n - feeBps - taxBps) / 10_000n;
  const expected = amountOut(net, reserves[0], reserves[1]);
  const minOut = withSlippage(expected, slippageBps);
  const { request } = await publicClient.simulateContract({
    account, address: curve, abi: curveAbi, functionName: 'buy',
    args: [quoteIn, minOut, account], value: quoteIn,
  });
  return wait(await walletClient.writeContract(request));
}

async function sellToken({ tokenAddress, curveAddress, amountTokens, slippageBps = 100 }) {
  requireWrites('launchWritesEnabled');
  const token = getAddress(tokenAddress);
  const curve = getAddress(curveAddress);
  const tokensIn = parseEther(String(amountTokens));
  const [allowance, reserves, feeBps, taxBps] = await Promise.all([
    publicClient.readContract({ address: token, abi: tokenAbi, functionName: 'allowance', args: [account, curve] }),
    publicClient.readContract({ address: curve, abi: curveAbi, functionName: 'getReserves' }),
    publicClient.readContract({ address: curve, abi: curveAbi, functionName: 'feeBps' }),
    publicClient.readContract({ address: curve, abi: curveAbi, functionName: 'creatorTaxBps' }),
  ]);
  if (allowance < tokensIn) {
    const approval = await walletClient.writeContract({ account, chain: chainFromConfig(), address: token, abi: tokenAbi, functionName: 'approve', args: [curve, maxUint256] });
    await wait(approval);
  }
  const gross = amountOut(tokensIn, reserves[1], reserves[0]);
  const expected = gross * (10_000n - feeBps - taxBps) / 10_000n;
  const { request } = await publicClient.simulateContract({
    account, address: curve, abi: curveAbi, functionName: 'sell',
    args: [tokensIn, withSlippage(expected, slippageBps), account],
  });
  return wait(await walletClient.writeContract(request));
}

async function claimNativeFees() {
  requireWrites('rewardWritesEnabled');
  if (!config.feeEscrowAddress) throw new Error('Fee escrow is not configured');
  const balance = await publicClient.readContract({ address: config.feeEscrowAddress, abi: feeEscrowAbi, functionName: 'balanceOf', args: [account] });
  if (balance === 0n) throw new Error('No claimable native fees');
  const { request } = await publicClient.simulateContract({ account, address: config.feeEscrowAddress, abi: feeEscrowAbi, functionName: 'claim' });
  return wait(await walletClient.writeContract(request));
}

function shortAddress(value = account) {
  return value ? `${value.slice(0, 6)}…${value.slice(-4)}` : '';
}

export {
  buyToken, claimNativeFees, config, connect, disconnect, formatEther, init, launchToken, listTokens,
  onWalletChange, shortAddress, signIn, sellToken, tokenDetails, uploadTokenImage, walletProfile,
};
